import { useState, useRef } from 'react';
import { Upload, FlaskConical, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { cutoverApi } from '../../services/cutoverApi';
import type { LabCorpImportPreviewDto, LabCorpImportResultDto, LabCorpImportRowDto } from '../../types/cutover';
import './LabCorpImportTab.css';

type Step = 'upload' | 'preview' | 'result';

export default function LabCorpImportTab() {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<LabCorpImportPreviewDto | null>(null);
  const [result, setResult] = useState<LabCorpImportResultDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setError(null);
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const data = await cutoverApi.previewLabCorpImport(file);
      setPreview(data);
      setStep('preview');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to parse file');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!preview) return;
    setLoading(true);
    setError(null);
    try {
      const data = await cutoverApi.executeLabCorpImport(preview.token);
      setResult(data);
      setStep('result');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep('upload');
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getStatusBadge = (row: LabCorpImportRowDto) => {
    if (row.isDuplicate) return <span className="labcorp-badge labcorp-badge-duplicate">Duplicate</span>;
    if (row.isInactive) return <span className="labcorp-badge labcorp-badge-inactive">Inactive</span>;
    return <span className="labcorp-badge labcorp-badge-active">Active</span>;
  };

  return (
    <div className="labcorp-import-tab">
      <div className="labcorp-import-header">
        <FlaskConical size={20} />
        <h2>LabCorp Import</h2>
        <p>Import LabCorp facility locations from the "Lab Corp Codes" sheet of the Clinic Matrix spreadsheet.</p>
      </div>

      {error && (
        <div className="labcorp-error">
          <XCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {step === 'upload' && (
        <div className="labcorp-upload-section">
          <label className="labcorp-file-label">
            <Upload size={16} />
            <span>Select Clinic Matrix .xlsx file</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              className="labcorp-file-input"
            />
          </label>
          {file && <p className="labcorp-filename">{file.name}</p>}
          <button
            type="button"
            className="labcorp-btn labcorp-btn-primary"
            onClick={handlePreview}
            disabled={!file || loading}
          >
            {loading ? 'Parsing...' : 'Preview'}
          </button>
        </div>
      )}

      {step === 'preview' && preview && (
        <div className="labcorp-preview-section">
          <div className="labcorp-summary">
            <span><strong>{preview.totalRows - preview.duplicateCount}</strong> to create</span>
            <span>·</span>
            <span><strong>{preview.inactiveCount}</strong> inactive (CLOSED)</span>
            <span>·</span>
            <span><strong>{preview.duplicateCount}</strong> duplicates (will be skipped)</span>
          </div>

          <div className="labcorp-table-wrapper">
            <table className="labcorp-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Address</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <tr key={row.code} className={row.isDuplicate ? 'labcorp-row-muted' : ''}>
                    <td>{row.code}</td>
                    <td>{row.name}</td>
                    <td>{row.address}</td>
                    <td>{row.city}</td>
                    <td>{row.state}</td>
                    <td>{getStatusBadge(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="labcorp-actions">
            <button type="button" className="labcorp-btn labcorp-btn-secondary" onClick={handleReset}>
              Back
            </button>
            <button
              type="button"
              className="labcorp-btn labcorp-btn-primary"
              onClick={handleExecute}
              disabled={loading}
            >
              {loading ? 'Importing...' : `Import ${preview.totalRows - preview.duplicateCount} Clinics`}
            </button>
          </div>
        </div>
      )}

      {step === 'result' && result && (
        <div className="labcorp-result-section">
          <div className="labcorp-result-counts">
            <div className="labcorp-result-item labcorp-result-created">
              <CheckCircle size={20} />
              <span><strong>{result.created}</strong> Created</span>
            </div>
            <div className="labcorp-result-item labcorp-result-skipped">
              <AlertCircle size={20} />
              <span><strong>{result.skipped}</strong> Skipped</span>
            </div>
            <div className="labcorp-result-item labcorp-result-failed">
              <XCircle size={20} />
              <span><strong>{result.failed}</strong> Failed</span>
            </div>
          </div>

          {result.errors.length > 0 && (
            <div className="labcorp-errors-list">
              <h3>Errors</h3>
              <ul>
                {result.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}

          <button type="button" className="labcorp-btn labcorp-btn-secondary" onClick={handleReset}>
            Import Another File
          </button>
        </div>
      )}
    </div>
  );
}
